import { useEffect, useLayoutEffect, useRef, useState, type DependencyList, type RefObject } from 'react'

/**
 * How much of the viewport's bottom edge each piece of bottom chrome has taken.
 *
 * Floating chrome that knows nothing about the page under it (toasts, the
 * install banner, the update pill) clears `--bottom-chrome-inset`. Until
 * 22.09.2026 that value was assembled from one CSS variable per component
 * type, which had two holes: a second bar of the same type overwrote the
 * first, and a bar that was not one of those types published nothing. The
 * destination editor's pinned Save/Review footer was the second case, and a
 * save toast landed on top of «Gå til publisering» (K14).
 *
 * Every owner now registers here and one value is written: the largest
 * occupancy, as `--bottom-owned-inset`. Each is measured from the viewport
 * BOTTOM, so it already contains everything below it and the result is a
 * max(), never a sum.
 */
const owners = new Map<symbol, number>()

function publish() {
  const root = document.documentElement
  const largest = Math.max(0, ...owners.values())
  if (largest > 0) root.style.setProperty('--bottom-owned-inset', `${largest}px`)
  else root.style.removeProperty('--bottom-owned-inset')
}

export function reportBottomOccupancy(owner: symbol, occupied: number) {
  const value = Math.max(0, Math.round(occupied))
  if (value > 0) owners.set(owner, value)
  else owners.delete(owner)
  publish()
}

export function releaseBottomOccupancy(owner: symbol) {
  owners.delete(owner)
  publish()
}

/**
 * `fixed`: the element is always attached to the bottom edge while visible
 * (StickyCtaBar, BottomTabBar).
 *
 * `sticky`: the element is `position: sticky; bottom: 0` inside a scrolling
 * page, so it only occupies the bottom edge while it is pinned there. Counted
 * while its bottom lies within one own-height of the viewport bottom, which
 * covers pinned and the last scroll step before the card ends. Further up it
 * is ordinary content and claims nothing, otherwise every toast on a short
 * page would float halfway up the screen.
 *
 * Hidden is read from the rect, not `offsetParent`: a fixed element has no
 * offsetParent ever, and that test once wrote 0px over a visible tab bar.
 */
export function measureBottomOccupancy(
  rect: Pick<DOMRect, 'top' | 'bottom' | 'height'>,
  viewportHeight: number,
  mode: 'fixed' | 'sticky',
): number {
  if (rect.height === 0 || rect.top >= viewportHeight) return 0
  if (mode === 'sticky' && rect.bottom < viewportHeight - rect.height) return 0
  return Math.max(0, Math.round(viewportHeight - rect.top))
}

const geometryEvent = 'eventripple:bottom-chrome-geometry'

function setHeightProperty(property: `--${string}`, value: string) {
  const style = document.documentElement.style
  if (style.getPropertyValue(property) === value) return
  if (value) style.setProperty(property, value)
  else style.removeProperty(property)
  // Moving an adjacent fixed bar does not trigger its ResizeObserver.
  window.dispatchEvent(new Event(geometryEvent))
}

function remeasure(owner: symbol, el: HTMLElement | null, mode: 'fixed' | 'sticky', active = true, heightProperty?: `--${string}`) {
  // A uniquely owned bar can expose its own height for adjacent stacked chrome.
  // Reuse this observer so stack offsets and occupied space cannot drift.
  if (heightProperty) {
    const height = el && active ? el.getBoundingClientRect().height : 0
    setHeightProperty(heightProperty, height > 0 ? `${height}px` : '')
  }
  if (!el || !active) return releaseBottomOccupancy(owner)
  reportBottomOccupancy(owner, measureBottomOccupancy(el.getBoundingClientRect(), window.innerHeight, mode))
}

export function useBottomChromeOccupancy(
  ref: RefObject<HTMLElement | null>,
  mode: 'fixed' | 'sticky' = 'fixed',
  deps: DependencyList = [],
  /** False while the bar is deliberately out of the way (the app nav slides off under an
   *  on-screen keyboard). A transform moves it without a resize event, so say it. */
  active = true,
  heightProperty?: `--${string}`,
) {
  const [owner] = useState(() => Symbol('bottom-chrome'))
  const tracked = useRef<{ el: HTMLElement | null; ro: ResizeObserver | null }>({ el: null, ro: null })
  const activeRef = useRef(active)
  const heightPropertyRef = useRef(heightProperty)
  useLayoutEffect(() => { activeRef.current = active; heightPropertyRef.current = heightProperty }, [active, heightProperty])

  // Follow the element, not the first render: a component can return a
  // skeleton first and mount its bar later (the destination editor does), and
  // an observer bound once on mount would never see the bar resize.
  useLayoutEffect(() => {
    const el = ref.current
    if (el === tracked.current.el) return
    tracked.current.ro?.disconnect()
    const ro = el && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => remeasure(owner, ref.current, mode, activeRef.current, heightPropertyRef.current))
      : null
    if (ro && el) ro.observe(el)
    tracked.current.el = el
    tracked.current.ro = ro
    remeasure(owner, el, mode, activeRef.current, heightPropertyRef.current)
  })

  // Position can move without the size changing (a `bottomOffset` prop), so
  // re-measure whenever the caller says its geometry inputs changed.
  useLayoutEffect(() => {
    remeasure(owner, ref.current, mode, active, heightProperty)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-supplied geometry inputs
  }, [owner, ref, mode, active, heightProperty, ...deps])

  useEffect(() => {
    const run = () => remeasure(owner, ref.current, mode, active, heightProperty)
    window.addEventListener('resize', run)
    window.addEventListener(geometryEvent, run)
    window.addEventListener('orientationchange', run)
    // Capture phase: the app scrolls an inner container, not the window, and a
    // sticky footer pins or unpins as that container scrolls.
    if (mode === 'sticky') document.addEventListener('scroll', run, { capture: true, passive: true })
    // Setup re-measures because a dependency change runs the previous cleanup,
    // which releases the claim, AFTER the layout effects above have measured.
    run()
    return () => {
      window.removeEventListener('resize', run)
      window.removeEventListener(geometryEvent, run)
      window.removeEventListener('orientationchange', run)
      if (mode === 'sticky') document.removeEventListener('scroll', run, { capture: true })
      releaseBottomOccupancy(owner)
      if (heightProperty) setHeightProperty(heightProperty, '')
    }
  }, [owner, ref, mode, active, heightProperty])

  useEffect(() => {
    const state = tracked.current
    return () => {
      state.ro?.disconnect()
      state.el = null
      state.ro = null
    }
  }, [])
}
